import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface BreadcrumbItem {
  label: string;
  url: string;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbsService {
  private readonly crumbsSubject = new BehaviorSubject<BreadcrumbItem[]>([]);
  public readonly crumbs$ = this.crumbsSubject.asObservable();

  // Holds an optional override for the current leaf label (e.g., job title)
  private currentLeafLabelOverride: string | null = null;

  public constructor(private readonly router: Router) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        // Recompute on each navigation
        this.refreshCrumbs(true);
      });
    // Compute immediately so components that mount this service
    // after the initial navigation still get data without waiting
    // for the next NavigationEnd.
    this.refreshCrumbs(false);
  }

  public setCurrentLeafLabel(label: string): void {
    this.currentLeafLabelOverride = label ?? null;
    this.refreshCrumbs(false);
  }

  // Insert a breadcrumb before the current leaf
  public insertBeforeLeaf(label: string, url: string): void {
    const crumbs = this.getCurrentCrumbs();
    if (crumbs.length > 0) {
      // Insert before the last crumb
      const newCrumbs = [...crumbs.slice(0, -1), { label, url }, crumbs[crumbs.length - 1]];
      this.crumbsSubject.next(newCrumbs);
    }
  }

  // Update a specific crumb by URL (useful to set a dynamic label for parent routes
  // like /vacancies/view-job/:id when the current route is a deeper leaf).
  public setCrumbLabel(url: string, label: string): void {
    const normalized = url?.startsWith('/') ? url : `/${url ?? ''}`;
    let crumbs = this.getCurrentCrumbs();
    if (!crumbs || crumbs.length === 0) {
      this.refreshCrumbs(false);
      crumbs = this.getCurrentCrumbs();
    }
    crumbs = crumbs.map(c => c.url === normalized ? { ...c, label } : c);
    this.crumbsSubject.next(crumbs);
  }

  public getCurrentCrumbs(): BreadcrumbItem[] {
    return this.crumbsSubject.getValue();
  }

  private buildCrumbs(route: ActivatedRouteSnapshot, url: string = '', crumbs: BreadcrumbItem[] = []): BreadcrumbItem[] {
    const routeUrl = route.url.map(segment => segment.path).join('/');
    const nextUrl = routeUrl ? `${url}/${routeUrl}` : url;

    const dataLabel = (route.data && route.data['breadcrumb']) as string | undefined;
    if (dataLabel) {
      const item: BreadcrumbItem = { label: dataLabel, url: nextUrl || '/' };
      crumbs.push(item);
    }

    if (route.firstChild) {
      return this.buildCrumbs(route.firstChild, nextUrl, crumbs);
    }

    // Apply leaf override only to last crumb
    if (crumbs.length > 0 && this.currentLeafLabelOverride) {
      crumbs = crumbs.map((c, idx) => idx === crumbs.length - 1 ? { ...c, label: this.currentLeafLabelOverride as string } : c);
    }
    return crumbs;
  }

  private refreshCrumbs(resetLeafOverride: boolean): void {
    if (resetLeafOverride) {
      this.currentLeafLabelOverride = null;
    }
    const root = this.router.routerState.snapshot.root;
    let crumbs = this.buildCrumbs(root);
    crumbs = this.maybeInjectParentCrumbs(crumbs);
    this.crumbsSubject.next(crumbs);
  }

  // Ensure intermediate parent routes (e.g., "/vacancies") appear as crumbs when navigating
  // to a deeper standalone route like "/vacancies/view-job/:id" even if the route configs
  // aren't nested as children.
  private maybeInjectParentCrumbs(crumbs: BreadcrumbItem[]): BreadcrumbItem[] {
    try {
      const fullPath = (this.router.url || '/').split('?')[0].replace(/^\/+/, '');
      if (!fullPath) { return crumbs; }

      const parts = fullPath.split('/');
      const toAdd: BreadcrumbItem[] = [];

      // Build incremental prefixes: "vacancies", "vacancies/view-job", ...
      for (let i = 1; i < parts.length; i++) {
        const prefix = parts.slice(0, i).join('/');
        const matching = this.router.config.find(r => this.pathMatchesPrefix(r.path || '', prefix));
        const label = (matching?.data as any)?.['breadcrumb'] as string | undefined;
        if (label) {
          const url = `/${prefix}`;
          if (!crumbs.some(c => c.url === url)) {
            toAdd.push({ label, url });
          }
        }
      }

      if (toAdd.length === 0) { return crumbs; }
      toAdd.sort((a, b) => a.url.length - b.url.length);
      return [...toAdd, ...crumbs];
    } catch {
      return crumbs;
    }
  }

  private pathMatchesPrefix(routePath: string, prefix: string): boolean {
    if (!routePath) { return false; }
    if (routePath === prefix) { return true; }
    // Handle parameterized routes like "vacancies/view-job/:id"
    const pattern = '^' + routePath
      .split('/')
      .map(seg => seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('/') + '$';
    try {
      const re = new RegExp(pattern);
      return re.test(prefix);
    } catch {
      return false;
    }
  }
}


